import { useMemo } from "react";
import { createFilter } from "react-select";
import moment from "moment-timezone";
import { Select } from "components/Select";
import { TIMEZONES } from "util/timezones";
import { zoneLabel } from "./publicBooking";

/** The US zones, listed first by their everyday names. */
const US_ZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
];

type ZoneOption = { value: string; label: string; city: string };

const zoneOption = (zone: string): ZoneOption => ({
  value: zone,
  label: zoneLabel(zone),
  city: (zone.split("/").pop() ?? zone).replace(/_/g, " "),
});

const byName = (a: ZoneOption, b: ZoneOption) =>
  a.label.localeCompare(b.label) || a.city.localeCompare(b.city);

// Search finds a zone by its name, its city or its id.
const filter = createFilter<ZoneOption>({
  stringify: (option) => `${option.label} ${option.data.city} ${option.value}`,
});

/**
 * The visitor's time zone: the app's searchable Select, with the US zones
 * first ("Eastern Time"), then every other zone by name and city ("Central
 * European Time", Berlin), each with its time now. The value is the IANA zone.
 */
export default function ZonePicker({
  inputId,
  value,
  onChange,
}: {
  inputId: string;
  value: string;
  onChange: (zone: string) => void;
}) {
  const groups = useMemo(() => {
    const others = TIMEZONES.filter((zone) => !US_ZONES.includes(zone));
    // A zone the list lacks (the visitor's own, say) is offered too.
    if (!US_ZONES.includes(value) && !others.includes(value))
      others.push(value);
    return [
      { label: "United States", options: US_ZONES.map(zoneOption) },
      {
        label: "Other time zones",
        options: others.map(zoneOption).sort(byName),
      },
    ];
  }, [value]);
  const selected = groups
    .flatMap((group) => group.options)
    .find((option) => option.value === value);

  return (
    // The forms plugin would draw a box around the search text.
    <div className="[&_input:focus]:ring-0 [&_input]:border-0 [&_input]:p-0">
      <Select
        inputId={inputId}
        options={groups}
        value={selected}
        onChange={(option: ZoneOption | null) =>
          option && onChange(option.value)
        }
        filterOption={filter}
        // Above the page's card, which clips what overflows its rounded
        // corners, and upward when there's no room below (as in a frame).
        menuPortalTarget={document.body}
        menuPosition="fixed"
        menuPlacement="auto"
        maxMenuHeight={280}
        formatOptionLabel={(
          option: ZoneOption,
          { context }: { context: "menu" | "value" }
        ) =>
          context === "menu" ? (
            <span className="flex w-full justify-between gap-3">
              <span>{option.label}</span>
              <span className="shrink-0 text-sm text-gray-400">
                {option.city} · {moment.tz(option.value).format("h:mma")}
              </span>
            </span>
          ) : (
            option.label
          )
        }
      />
    </div>
  );
}
